import React from "react";

/** One keycap button of the naming card's pair: a key, then the word. A muted
 *  one is Cancel's kind; `confirm` is the accent twin. */
export function KeyAction({
  keycap,
  icon: Icon,
  confirm = false,
  danger = false,
  busy = false,
  disabled = false,
  autoFocus = false,
  onClick,
  children,
}: {
  /** The key that also presses it: "esc", "↵". */
  keycap?: string;
  /** Where no key presses it, an icon in the keycap's place. */
  icon?: React.ComponentType<{ className?: string }>;
  confirm?: boolean;
  danger?: boolean;
  busy?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    // eslint-disable-next-line no-restricted-syntax -- the naming card's keycap button
    <button
      type="button"
      autoFocus={autoFocus}
      className={
        confirm
          ? `naming-create${danger ? " naming-danger" : ""}`
          : "naming-cancel"
      }
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      {busy ? (
        <span className="naming-spin" aria-hidden />
      ) : keycap ? (
        <kbd className="naming-key">{keycap}</kbd>
      ) : (
        Icon && (
          <span className="naming-key" aria-hidden>
            <Icon className="icon-xs" />
          </span>
        )
      )}
      {children}
    </button>
  );
}

/** The naming card's keycap pair: "esc Cancel" and "↵ {confirmText}". */
export function KeyActions({
  confirmText,
  cancelText = "Cancel",
  danger = false,
  busy = false,
  disabled = false,
  cancelDisabled = false,
  autoFocus = false,
  className,
  onConfirm,
  onCancel,
  onMouseDown,
}: {
  confirmText: string;
  cancelText?: string;
  danger?: boolean;
  busy?: boolean;
  disabled?: boolean;
  cancelDisabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  onConfirm: () => void;
  onCancel: () => void;
  onMouseDown?: React.MouseEventHandler;
}): React.JSX.Element {
  return (
    <span className={className} onMouseDown={onMouseDown}>
      <KeyAction keycap="esc" onClick={onCancel} disabled={cancelDisabled}>
        {cancelText}
      </KeyAction>
      <KeyAction
        keycap="↵"
        confirm
        danger={danger}
        busy={busy}
        disabled={disabled}
        autoFocus={autoFocus}
        onClick={onConfirm}
      >
        {confirmText}
      </KeyAction>
    </span>
  );
}

/** A panel's footer of keycap buttons: 40px, on its own line under the body. */
export function KeyFooter({
  className = "",
  children,
}: {
  className?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className={`shrink-0 flex items-center gap-1.5 h-10 px-3 border-t border-border ${className}`}
    >
      {children}
    </div>
  );
}
